import React, { useState, useEffect } from 'react';
import {
    Plus,
    Search,
    Filter,
    Edit3,
    Trash2,
    AlertTriangle,
    CheckCircle2,
    Package,
    Image,
    X,
    Loader2
} from 'lucide-react';
import productService from '../../services/productService';
import Toast from '../common/Toast';

const AdminInventory = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('All');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [toast, setToast] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        category: '',
        subcategory: '',
        description: '',
        price: '',
        stock: '',
        image: null
    });

    const handleSearch = React.useCallback(async () => {
        try {
            setLoading(true);
            const results = await productService.searchProducts(searchTerm);
            setProducts(results);
        } catch (err) {
            console.error('Search error:', err);
            setToast({ message: 'Erreur lors de la recherche', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [searchTerm]);

    const loadData = React.useCallback(async (isSilent = false) => {
        if (!isSilent) setLoading(true);
        try {
            const [productsData, categoriesData] = await Promise.all([
                productService.getProducts(),
                productService.getCategories()
            ]);
            setProducts(productsData);
            setCategories(categoriesData);
        } catch (err) {
            console.error('Error fetching inventory:', err);
            if (!isSilent) setToast({ message: 'Erreur lors du chargement des données', type: 'error' });
        } finally {
            if (!isSilent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm) {
                handleSearch();
            } else {
                loadData();
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, handleSearch, loadData]);

    // Background polling every 60 seconds
    useEffect(() => {
        // Don't poll while searching or while a modal is open
        if (searchTerm || isModalOpen || isDeleteModalOpen) return;

        const interval = setInterval(() => {
            loadData(true);
        }, 60000);

        return () => clearInterval(interval);
    }, [searchTerm, isModalOpen, isDeleteModalOpen, loadData]);



    //const allCategories = ['All', ...categories.map(c => c.name)];
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const data = new FormData();
            data.append('name', formData.name);
            data.append('description', formData.description);
            data.append('price', formData.price);
            data.append('stock', formData.stock);

            // For editing, only append image if it's a new file object
            if (formData.image instanceof File) {
                data.append('image', formData.image);
            }

            if (formData.category) {
                data.append('category', formData.category);
            }
            if (formData.subcategory) {
                data.append('subcategory', formData.subcategory);
            }

            if (editingProduct) {
                await productService.updateProduct(editingProduct.id, data);
                setToast({ message: 'Produit mis à jour avec succès', type: 'success' });
            } else {
                await productService.createProduct(data);
                setToast({ message: 'Produit ajouté avec succès', type: 'success' });
            }

            setIsModalOpen(false);
            setEditingProduct(null);
            setFormData({ name: '', category: '', subcategory: '', description: '', price: '', stock: '', image: null });
            loadData();
        } catch (err) {
            console.error('Error saving product:', err);
            setToast({ message: `Erreur: ${err.message || 'Action impossible'}`, type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (product) => {
        setEditingProduct(product);
        setFormData({
            name: product.name,
            category: product.category, // Assuming backend sends ID now? Or verify serializer output. Serializer sends ID in 'category' field.
            subcategory: product.subcategory || '',
            description: product.description || '',
            price: product.price,
            stock: product.stock,
            image: product.image // String URL, not a File object
        });
        setIsModalOpen(true);
    };

    const handleDeleteClick = (product) => {
        setProductToDelete(product);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!productToDelete) return;
        setIsSubmitting(true);
        try {
            await productService.deleteProduct(productToDelete.id);
            setToast({ message: 'Produit supprimé avec succès', type: 'success' });
            setIsDeleteModalOpen(false);
            setProductToDelete(null);
            loadData();
        } catch (err) {
            console.error('Error deleting product:', err);
            setToast({ message: 'Erreur lors de la suppression', type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, image: file });
        }
    };

    const filteredProducts = products.filter(p => {
        if (filterCategory === 'All') return true;

        // Resolve category name safely
        let catName = '';
        if (typeof p.category === 'object' && p.category !== null) {
            catName = p.category.name || '';
        } else {
            const cat = categories.find(c => c.id === p.category);
            catName = cat ? cat.name : '';
        }

        return catName === filterCategory;
    });

    const getStockStatus = (stock) => {
        if (stock === 0) return { label: 'Épuisé', color: 'text-red-600 bg-red-50', icon: AlertTriangle };
        if (stock <= 5) return { label: 'Stock Faible', color: 'text-amber-600 bg-amber-50', icon: AlertTriangle };
        return { label: 'En Stock', color: 'text-emerald-600 bg-emerald-50', icon: CheckCircle2 };
    };

    if (loading) return (
        <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand"></div>
        </div>
    );

    return (
        <div className="space-y-10">
            {/* Header Actions */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-3xl font-black text-green-950 mb-2">Gestion d'Inventaire</h1>
                    <p className="text-slate-500">Gérez vos produits, catégories et niveaux de stock.</p>
                </div>
                <button
                    onClick={() => {
                        setEditingProduct(null);
                        setFormData({ name: '', category: '', subcategory: '', description: '', price: '', stock: '', image: null });
                        setIsModalOpen(true);
                    }}
                    className="h-16 px-8 bg-brand text-white rounded-2xl font-black text-sm shadow-xl shadow-brand/20 flex items-center gap-3 hover:bg-green-900 transition-all"
                >
                    <Plus className="w-5 h-5" />
                    Ajouter un Produit
                </button>
            </div>

            {/* Filters Bar */}
            <div className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col lg:flex-row items-center gap-6">
                <div className="relative flex-grow w-full">
                    <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                    <input
                        type="text"
                        placeholder="Rechercher un produit..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full h-16 pl-14 pr-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand transition-all outline-none font-medium"
                    />
                </div>
                <div className="flex items-center gap-4 w-full lg:w-auto">
                    <div className="relative flex-grow lg:flex-grow-0">
                        <Filter className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                        <select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            className="w-full h-16 pl-14 pr-12 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand transition-all outline-none font-bold text-slate-600 appearance-none min-w-[200px]"
                        >
                            <option value="All">Toutes les catégories</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.name}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Inventory Table */}
            <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-slate-50/50 text-left">
                                <th className="px-8 py-6 text-xs font-black text-slate-400 uppercase tracking-widest">Produit</th>
                                <th className="px-8 py-6 text-xs font-black text-slate-400 uppercase tracking-widest">Catégorie</th>
                                <th className="px-8 py-6 text-xs font-black text-slate-400 uppercase tracking-widest text-center">Stock</th>
                                <th className="px-8 py-6 text-xs font-black text-slate-400 uppercase tracking-widest text-center">Status</th>
                                <th className="px-8 py-6 text-xs font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredProducts.map((product) => {
                                const status = getStockStatus(product.stock);
                                return (
                                    <tr key={product.id} className="hover:bg-slate-50/30 transition-all">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-16 h-16 rounded-2xl overflow-hidden bg-slate-100 relative group">
                                                    <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform group-hover:scale-110" />
                                                </div>
                                                <div>
                                                    <p className="font-bold text-green-950">{product.name}</p>
                                                    <p className="text-sm font-bold text-brand">{Math.floor(product.price).toLocaleString('fr-FR')} Fcfa</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className="px-4 py-2 bg-slate-100 text-slate-600 text-xs font-bold rounded-full uppercase tracking-widest">
                                                {(() => {
                                                    if (typeof product.category === 'object' && product.category !== null) {
                                                        return product.category.name || 'Inconnue';
                                                    }
                                                    const cat = categories.find(c => c.id === product.category);
                                                    return cat ? cat.name : 'Inconnue';
                                                })()}
                                            </span>
                                            {product.subcategory && (
                                                <span className="ml-2 px-2 py-1 bg-slate-50 text-slate-400 text-[10px] font-bold rounded-full">
                                                    {(() => {
                                                        if (typeof product.subcategory === 'object' && product.subcategory !== null) {
                                                            return product.subcategory.name || '';
                                                        }
                                                        const catId = typeof product.category === 'object' ? product.category?.id : product.category;
                                                        const cat = categories.find(c => c.id === catId);
                                                        const sub = cat?.subcategories?.find(s => s.id === product.subcategory);
                                                        return sub ? sub.name : '';
                                                    })()}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <div className="flex flex-col items-center">
                                                <span className="text-lg font-black text-green-950">{product.stock || 0}</span>
                                                <p className="text-[10px] font-bold text-slate-400 uppercase">Unités</p>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className={`flex items-center justify-center gap-2 px-4 py-2 rounded-full font-black text-[10px] uppercase tracking-widest ${status.color} mx-auto w-fit`}>
                                                <status.icon className="w-3 h-3" />
                                                {status.label}
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleEdit(product)}
                                                    className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-brand hover:text-white transition-all shadow-sm"
                                                >
                                                    <Edit3 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteClick(product)}
                                                    className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white transition-all shadow-sm"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {filteredProducts.length === 0 && (
                    <div className="py-20 text-center">
                        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300">
                            <Package className="w-10 h-10" />
                        </div>
                        <p className="text-slate-500 font-bold">Aucun produit ne correspond à votre recherche.</p>
                    </div>
                )}
            </div>
            {/* Add Product Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-green-950/20 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto">
                        <form onSubmit={handleSubmit} className="p-10">
                            <div className="flex justify-between items-start mb-8">
                                <div>
                                    <h2 className="text-2xl font-black text-green-950 mb-1">
                                        {editingProduct ? 'Modifier le Produit' : 'Ajouter un Produit'}
                                    </h2>
                                    <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">
                                        {editingProduct ? 'Modifiez les informations du produit existant.' : 'Remplissez les détails du nouveau produit.'}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="w-12 h-12 rounded-2xl bg-slate-50 text-slate-400 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-all"
                                >
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Nom du Produit</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full h-14 px-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand font-bold text-green-950 outline-none"
                                            placeholder="Ex: Fauteuil Club"
                                        />
                                    </div>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Catégorie</label>
                                            <select
                                                required
                                                value={formData.category}
                                                onChange={(e) => {
                                                    setFormData({
                                                        ...formData,
                                                        category: parseInt(e.target.value),
                                                        subcategory: '' // Reset subcategory when category changes
                                                    });
                                                }}
                                                className="w-full h-14 px-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand font-bold text-green-950 outline-none"
                                            >
                                                <option value="">Sélectionnez une catégorie</option>
                                                {categories.map(cat => (
                                                    <option key={cat.id} value={cat.id}>
                                                        {cat.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Sous-catégorie</label>
                                            <select
                                                value={formData.subcategory}
                                                onChange={(e) => setFormData({ ...formData, subcategory: parseInt(e.target.value) })}
                                                disabled={!formData.category}
                                                className="w-full h-14 px-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand font-bold text-green-950 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <option value="">Sélectionnez une sous-catégorie</option>
                                                {formData.category && categories
                                                    .find(c => c.id === parseInt(formData.category))
                                                    ?.subcategories?.map(sub => (
                                                        <option key={sub.id} value={sub.id}>
                                                            {sub.name}
                                                        </option>
                                                    ))
                                                }
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Description</label>
                                    <textarea
                                        required
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full min-h-[120px] p-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand font-bold text-green-950 outline-none resize-none"
                                        placeholder="Description détaillée du produit..."
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Prix (Fcfa)</label>
                                        <input
                                            type="number"
                                            required
                                            value={formData.price}
                                            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                            className="w-full h-14 px-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand font-bold text-green-950 outline-none"
                                            placeholder="0"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Stock Initial</label>
                                        <input
                                            type="number"
                                            required
                                            value={formData.stock}
                                            onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                                            className="w-full h-14 px-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand font-bold text-green-950 outline-none"
                                            placeholder="0"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Image du Produit</label>
                                    <div className="relative group">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            className="hidden"
                                            id="product-image"
                                            required={!formData.image && !editingProduct}
                                        />
                                        <label
                                            htmlFor="product-image"
                                            className="flex flex-col items-center justify-center w-full min-h-[160px] border-2 border-dashed border-slate-200 rounded-[2rem] hover:border-brand hover:bg-brand/5 cursor-pointer transition-all group"
                                        >
                                            {formData.image ? (
                                                <div className="flex flex-col items-center gap-4">
                                                    <div className="w-20 h-20 rounded-2xl overflow-hidden shadow-lg border-4 border-white">
                                                        <img
                                                            src={formData.image instanceof File ? URL.createObjectURL(formData.image) : formData.image}
                                                            alt="Preview"
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </div>
                                                    <div className="flex items-center gap-4 text-brand font-bold">
                                                        <Image className="w-5 h-5" />
                                                        <span className="text-xs truncate max-w-[200px]">
                                                            {formData.image instanceof File ? formData.image.name : 'Image actuelle'}
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 mb-4 group-hover:scale-110 transition-transform">
                                                        <Plus className="w-6 h-6" />
                                                    </div>
                                                    <p className="font-bold text-slate-400">Cliquez pour ajouter une image</p>
                                                    <p className="text-[10px] uppercase font-black tracking-widest text-slate-300 mt-2">JPG, PNG, WEBP (Max 5MB)</p>
                                                </>
                                            )}
                                        </label>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full h-16 bg-brand text-white rounded-2xl font-black shadow-xl shadow-brand/20 flex items-center justify-center gap-3 hover:bg-green-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            Envoi en cours...
                                        </>
                                    ) : (
                                        <>
                                            {editingProduct ? <Edit3 className="w-5 h-5" /> : <Package className="w-5 h-5" />}
                                            {editingProduct ? 'Mettre à jour le Produit' : 'Enregistrer le Produit'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 bg-red-950/20 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden p-10 animate-in zoom-in-95 duration-300">
                        <div className="w-20 h-20 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center mx-auto mb-6">
                            <Trash2 className="w-10 h-10" />
                        </div>
                        <h3 className="text-2xl font-black text-green-950 text-center mb-2">Confirmer la suppression</h3>
                        <p className="text-slate-500 text-center mb-8">
                            Êtes-vous sûr de vouloir supprimer <span className="font-bold text-green-950">"{productToDelete?.name}"</span> ? Cette action est irréversible.
                        </p>
                        <div className="flex gap-4">
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="flex-1 h-14 bg-slate-50 text-slate-600 rounded-2xl font-bold hover:bg-slate-100 transition-all"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={isSubmitting}
                                className="flex-1 h-14 bg-red-500 text-white rounded-2xl font-bold shadow-lg shadow-red-200 hover:bg-red-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Supprimer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};
export default AdminInventory;
